import { MfsBaseProperty, MfsCollectionCommonProperty, MfsSystemProperty, MfsRequiredProperty } from './mfsTypes';

/** Represents the traits of Mfs Properties. */
export const enum MfsPropertyTrait {
  None,
  /** Value assigned by Mfs store. */
  System = 1 << 1,
  /** Value provided by the host application. */
  App = 1 << 2,
  /** Once set, cannot be modified via any store method. */
  Immutable = 1 << 3,
  /** Value required to be specified by host application for all items including collections. */
  Required = 1 << 4,
  /** Value required to be specified by host applicaton for only collections. */
  CollectionRequired = 1 << 5,
  /** Property can be set by any app, deleted only by the item-owner. */
  Mutable = 1 << 6,
  /** Property can be set/deleted only by the item-owner. */
  MutableIfOwner = 1 << 7,
  /** Property has multiple values (e.g. array, dictionary), cannot be modified by set or delete operations */
  MultiValue = 1 << 8,
  /** Property has multiple unique values. */
  Unique = 1 << 9,
}

/** The record of all base properties. These are the well-known property names of MfsItems. */
export const MfsBaseProperties: Record<MfsBaseProperty, MfsPropertyTrait> = {
  id: MfsPropertyTrait.System | MfsPropertyTrait.Immutable,
  label: MfsPropertyTrait.App | MfsPropertyTrait.Required | MfsPropertyTrait.Immutable,
  secondaryLabels:
    MfsPropertyTrait.App | MfsPropertyTrait.MultiValue | MfsPropertyTrait.Unique | MfsPropertyTrait.Mutable,
  labelId: MfsPropertyTrait.App | MfsPropertyTrait.MutableIfOwner,
  displayText: MfsPropertyTrait.App | MfsPropertyTrait.Required | MfsPropertyTrait.Mutable,
  kind: MfsPropertyTrait.App | MfsPropertyTrait.Immutable | MfsPropertyTrait.CollectionRequired,
};

/** The record of all system properties. */
export const MfsSystemProperties: Record<MfsSystemProperty, boolean> = {
  id: true,
};

/** The record of all required properties to be supplied by the application for creating Mfs Items */
export const MfsRequiredProperties: Record<MfsRequiredProperty, boolean> = {
  label: true,
  displayText: true,
};

/** The record of all well-known collection properties. */
export const MfsCollectionProperties: Record<MfsCollectionCommonProperty, boolean> = {
  kind: true,
};

/** The record of all necessary properties to be considered an Mfs Item */
export const RequiredMfsItemProperties: Record<MfsSystemProperty | MfsRequiredProperty, boolean> = {
  ...MfsSystemProperties,
  ...MfsRequiredProperties,
};

/** Whether the property is a well-known property (either system assigned or common) */
export function isBaseProperty(propertyName: string): boolean {
  return propertyName in MfsBaseProperties;
}

/** Whether the property is a required, system assigned property */
export const isSystemProperty = (propertyName: string): propertyName is MfsSystemProperty =>
  propertyName in MfsSystemProperties;

/** Whether the property is a required app-defined property of a Mfs Item. */
export const isRequiredProperty = (propertyName: string): boolean => propertyName in MfsRequiredProperties;

/** Whether the property is an immutable property. */
export const isImmutableProperty = (propertyName: string): propertyName is MfsBaseProperty =>
  hasTraits(propertyName, MfsPropertyTrait.Immutable);

/** Whether the base property is {@link MfsPropertyTrait.Mutable}. */
export const isMutableProperty = (propertyName: string): propertyName is MfsBaseProperty =>
  hasTraits(propertyName, MfsPropertyTrait.Mutable);

/** Whether the base property is {@link MfsPropertyTrait.MutableIfOwner}. */
export const isOwnerAppManaged = (propertyName: string): propertyName is MfsBaseProperty =>
  hasTraits(propertyName, MfsPropertyTrait.MutableIfOwner);

/** Checks whether the property has the specified traits. */
export const hasTraits = (propertyName: string, trait: MfsPropertyTrait): propertyName is MfsBaseProperty =>
  isMfsBaseProperty(propertyName) && (MfsBaseProperties[propertyName] & trait) === trait;

/** Type-guard, since IsBaseProperty is used while writing to read-only properties. */
export function isMfsBaseProperty(propertyName: string): propertyName is MfsBaseProperty {
  return isBaseProperty(propertyName);
}

/** Gets properties with all of the given traits. */
export function getPropertiesWith(traits: MfsPropertyTrait, props?: Record<string, MfsPropertyTrait>): string[] {
  return [
    ...Object.entries(props ?? MfsBaseProperties)
      .filter((entry) => (entry[1] & traits) === traits)
      .map((entry) => entry[0]),
  ];
}

/** Gets properties with any of the given traits. */
export function getPropertiesWithAny(traits: MfsPropertyTrait[], props?: Record<string, MfsPropertyTrait>): string[] {
  return [
    ...Object.entries(props ?? MfsBaseProperties)
      .filter((entry) => traits.some((trait) => (entry[1] & trait) == trait))
      .map((entry) => entry[0]),
  ];
}
